/**
 * Chrome strings, behind an accessor.
 *
 * Authority: bead scope-njw.
 *
 * **No i18n library in v1 — the accessor is the seam.** A library brings a
 * loader, a plural engine and a context stack that nothing here needs yet; what
 * v1 actually needs is that no component holds a literal, so that adding the
 * second locale is a data change. `t()` is that guarantee and it is 30 lines.
 *
 * Placeholders, not concatenation: `t('shell.signedInAs', { email })` keeps the
 * sentence in one string. `t('a') + email` puts a word order into TypeScript,
 * where no translator can reach it.
 */
import en from '../i18n/en.json' with { type: 'json' }

/**
 * **The staff strings are not in this module's graph.** They are a type here
 * and a runtime import only in `src/app/main.staff.tsx`.
 *
 * `en.json` used to carry every key, so the public bundle shipped 118 admin and
 * staff strings — column headings, the bulk-read alert, the whole shape of the
 * audit console — to anyone who fetched the asset. Splitting the registry
 * stopped the staff *code* reaching the public origin; this stops its
 * vocabulary. `typeof import(...)` is erased at compile time, so naming the file
 * for the key union costs the public bundle nothing.
 */
type StaffMessages = typeof import('../i18n/en.staff.json')

export type MessageKey = keyof typeof en | keyof StaffMessages
export type MessageValues = Readonly<Record<string, string | number>>

const MESSAGES: Record<string, string> = { ...en }

/**
 * Merge an origin's own strings in. Called once, by the entry point that has
 * them — today only `main.staff.tsx`.
 */
export function registerMessages(extra: Readonly<Record<string, string>>): void {
  Object.assign(MESSAGES, extra)
}

const PLACEHOLDER = /\{([a-zA-Z][a-zA-Z0-9]*)\}/g

export class MissingMessageError extends Error {}

/**
 * The string for `key`, with `{name}` placeholders filled from `values`.
 *
 * A missing key throws rather than rendering the key: a key rendered into a
 * page is a defect that ships, and a throw is a defect that fails a test. The
 * key space is `MessageKey`, so this is unreachable from typechecked code and
 * only fires for a key built at runtime.
 */
export function t(key: MessageKey, values: MessageValues = {}): string {
  const template = MESSAGES[key]
  if (template === undefined) throw new MissingMessageError(`no message for "${key}"`)

  return template.replace(PLACEHOLDER, (_match: string, name: string) => {
    const value = values[name]
    if (value === undefined) throw new MissingMessageError(`${key}: no value for "{${name}}"`)

    return String(value)
  })
}

/** Placeholder names this message expects. Used by the suite to prove every one is fillable. */
export function placeholdersOf(key: MessageKey): string[] {
  const template = MESSAGES[key] ?? ''

  return [...template.matchAll(PLACEHOLDER)].map((match) => match[1] ?? '')
}

export function messageKeys(): MessageKey[] {
  return Object.keys(MESSAGES) as MessageKey[]
}
